import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaChevronLeft, FaChevronRight, FaPlus } from "react-icons/fa";
import AddAvailabilityDrawer from "./Availability/AddAvailabilityDrawer";
import EditAvailabilityDrawer from "./Availability/EditAvailabilityDrawer";
import DeleteAvailabilityModal from "./Availability/DeleteAvailabilityModal";

const DAY_LABELS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
/** First hour shown on the Y-axis (inclusive). */
const HOUR_START = 7;
/** Last hour label on the Y-axis (inclusive). Grid spans through the end of this hour. */
const HOUR_LAST_LABEL = 21;
/** Exclusive end of the visible day range (9 PM label → grid ends at 10 PM). */
const HOUR_END_EXCLUSIVE = HOUR_LAST_LABEL + 1;
const HOURS = Array.from(
  { length: HOUR_LAST_LABEL - HOUR_START + 1 },
  (_, i) => HOUR_START + i,
);
const TOTAL_MINUTES = (HOUR_END_EXCLUSIVE - HOUR_START) * 60;

const startOfWeek = (date) => {
  const d = moment(date).startOf("day");
  return d.subtract(d.day(), "days");
};

const formatHourLabel = (hour) => {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

/** Normalize any date-like value to YYYY-MM-DD for week grouping. */
const toDateKey = (date) => {
  if (!date) return "";
  if (moment.isMoment(date) && date.isValid()) return date.format("YYYY-MM-DD");
  const raw = String(date).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = moment(
    raw,
    [moment.ISO_8601, "YYYY-MM-DD", "MM/DD/YYYY", "M/D/YYYY"],
    true,
  );
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const normalizeTime = (time) => {
  if (time == null || time === "") return "";
  if (moment.isMoment(time) && time.isValid()) return time.format("HH:mm:ss");
  const raw = String(time).trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (match) {
    return `${match[1].padStart(2, "0")}:${match[2]}:${match[3] || "00"}`;
  }
  return "";
};

const slotMoment = (date, time) => {
  const dateKey = toDateKey(date);
  const timeKey = normalizeTime(time);
  if (!dateKey || !timeKey) return moment.invalid();
  return moment(`${dateKey}T${timeKey}`);
};

const formatDisplayTime = (date, time) => {
  const m = slotMoment(date, time);
  return m.isValid() ? m.format("h:mm A") : "";
};

const durationHours = (startDate, startTime, endTime) => {
  const start = slotMoment(startDate, startTime);
  let end = slotMoment(startDate, endTime);
  if (!start.isValid() || !end.isValid()) return 0;
  if (!end.isAfter(start)) end = end.clone().add(1, "day");
  return Math.max(0, end.diff(start, "minutes") / 60);
};

const formatDurationLabel = (hours) => {
  if (!hours) return "";
  if (Number.isInteger(hours)) return `${hours}h`;
  const whole = Math.floor(hours);
  const mins = Math.round((hours - whole) * 60);
  if (!whole) return `${mins}m`;
  return mins ? `${whole}h ${mins}m` : `${whole}h`;
};

/**
 * Position a block within the day column so top aligns to start time
 * and height scales with duration (1 hour = one row).
 */
const blockStyle = (startDate, startTime, endTime) => {
  const start = slotMoment(startDate, startTime);
  let end = slotMoment(startDate, endTime);
  if (!start.isValid() || !end.isValid()) return null;
  if (!end.isAfter(start)) end = end.clone().add(1, "day");

  const dateKey = toDateKey(startDate);
  const dayStart = moment(dateKey).hour(HOUR_START).minute(0).second(0);
  const dayEnd = moment(dateKey).hour(HOUR_END_EXCLUSIVE).minute(0).second(0);

  const clampedStart = moment.max(start, dayStart);
  const clampedEnd = moment.min(end, dayEnd);
  if (!clampedEnd.isAfter(clampedStart)) return null;

  const topMinutes = clampedStart.diff(dayStart, "minutes");
  const heightMinutes = Math.max(clampedEnd.diff(clampedStart, "minutes"), 20);

  return {
    top: `${(topMinutes / TOTAL_MINUTES) * 100}%`,
    height: `${(heightMinutes / TOTAL_MINUTES) * 100}%`,
  };
};

const ProviderAvailabilityTab = ({
  getProviderId,
  canManage = true,
  hideSectionTitle = false,
}) => {
  const user = useSelector((state) => state?.user);
  const authToken =
    user?.user?.token || localStorage.getItem("token") || localStorage.getItem("user_id");
  const providerId = typeof getProviderId === "function" ? getProviderId() : getProviderId;

  const [weekAnchor, setWeekAnchor] = useState(() => startOfWeek(new Date()));
  const [slots, setSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteEventData, setDeleteEventData] = useState(null);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => moment(weekAnchor).add(i, "days")),
    [weekAnchor],
  );

  const weekRangeLabel = useMemo(() => {
    const start = weekDays[0];
    const end = weekDays[6];
    if (start.month() === end.month()) {
      return `${start.format("MMM D")} – ${end.format("D, YYYY")}`;
    }
    if (start.year() === end.year()) {
      return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
    }
    return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
  }, [weekDays]);

  const fetchAvailability = useCallback(async () => {
    if (!providerId) {
      setSlots([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const timezone = moment.tz.guess();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/availability/${providerId}?timezone=${timezone}`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );

      if (response.data.success && Array.isArray(response.data.data)) {
        setSlots(response.data.data);
      } else {
        setSlots([]);
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
      setSlots([]);
      toast.error("Error fetching availability data");
    } finally {
      setIsLoading(false);
    }
  }, [authToken, providerId]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const weekSlots = useMemo(() => {
    const startKey = weekDays[0].format("YYYY-MM-DD");
    const endKey = weekDays[6].format("YYYY-MM-DD");
    return slots.filter((slot) => {
      const date = toDateKey(slot.startDate);
      return date && date >= startKey && date <= endKey;
    });
  }, [slots, weekDays]);

  const weekStats = useMemo(() => {
    let totalHours = 0;
    const daysWithAvailability = new Set();
    weekSlots.forEach((slot) => {
      const hours = durationHours(slot.startDate, slot.startTime, slot.endTime);
      totalHours += hours;
      const dateKey = toDateKey(slot.startDate);
      if (hours > 0 && dateKey) daysWithAvailability.add(dateKey);
    });
    const rounded = Math.round(totalHours * 10) / 10;
    const hoursLabel =
      Number.isInteger(rounded) || rounded % 1 === 0
        ? `${Math.round(rounded)}`
        : `${rounded}`;
    return {
      hours: hoursLabel,
      days: daysWithAvailability.size,
    };
  }, [weekSlots]);

  const slotsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach((day) => {
      map[day.format("YYYY-MM-DD")] = [];
    });
    weekSlots.forEach((slot) => {
      const dateKey = toDateKey(slot.startDate);
      if (!dateKey || !map[dateKey]) return;
      map[dateKey].push(slot);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) =>
        normalizeTime(a.startTime).localeCompare(normalizeTime(b.startTime)),
      );
    });
    return map;
  }, [weekDays, weekSlots]);

  const goPrev = () => setWeekAnchor((prev) => moment(prev).subtract(7, "days"));
  const goNext = () => setWeekAnchor((prev) => moment(prev).add(7, "days"));
  const goToday = () => setWeekAnchor(startOfWeek(new Date()));

  const openAddForDay = (dayMoment, clickEvent) => {
    if (!canManage) return;
    const dateStr = dayMoment.format("YYYY-MM-DD");

    let startTime = null;
    let endTime = null;
    if (clickEvent?.currentTarget && typeof clickEvent.clientY === "number") {
      const rect = clickEvent.currentTarget.getBoundingClientRect();
      if (rect.height > 0) {
        const ratio = Math.max(
          0,
          Math.min(1, (clickEvent.clientY - rect.top) / rect.height),
        );
        const startMinutes =
          Math.floor((ratio * TOTAL_MINUTES) / 15) * 15;
        const endMinutes = Math.min(startMinutes + 60, TOTAL_MINUTES);
        const toHms = (mins) => {
          const hour = HOUR_START + Math.floor(mins / 60);
          const minute = mins % 60;
          return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`;
        };
        startTime = toHms(startMinutes);
        if (endMinutes > startMinutes) endTime = toHms(endMinutes);
      }
    }

    // Default window when opened from the toolbar (no slot click).
    if (!startTime) {
      startTime = "09:00:00";
      endTime = "10:00:00";
    }

    setSelectedEvent({
      startDate: dateStr,
      startTime,
      endTime,
      endType: "never",
      isRecurring: false,
      recurringInterval: 1,
      recurringUnit: "week",
      selectedDays: [dayMoment.format("dddd").toLowerCase()],
      endOccurrences: null,
      endsOn: null,
      title: "My Availability",
      name: "My Availability",
      notes: "",
    });
    setShowAddModal(true);
  };

  const openEdit = (slot) => {
    if (!canManage) return;
    setSelectedEvent({
      ...slot,
      id: slot.id,
      startDate: `${slot.startDate}T${String(slot.startTime || "09:00:00").slice(0, 8)}`,
      startTime: slot.startTime,
      endTime: slot.endTime,
      endType: slot.endType || "never",
      isRecurring: slot.isRecurring || false,
      recurringInterval: slot.recurringInterval || 1,
      recurringUnit: slot.recurringUnit || "week",
      selectedDays: slot.selectedDays || [],
      endOccurrences: slot.endOccurrences,
      endsOn: slot.endsOn,
      seriesId: slot.seriesId,
    });
    setShowEditModal(true);
  };

  const handleAddAvailability = async (availabilityData) => {
    try {
      setIsSubmitting(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/availability/${providerId}`,
        { ...availabilityData, timezone: moment.tz.guess() },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      if (response.data.success) {
        toast.success("Availability added successfully.");
        setShowAddModal(false);
        setSelectedEvent(null);
        await fetchAvailability();
      } else {
        toast.error(response.data.message || "Failed to add availability");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to add availability. Make sure it does not overlap existing blocks.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAvailability = async (availabilityData) => {
    const updateAll = availabilityData?.updateAll;
    const apiUrl = updateAll
      ? `availability/recurring/${availabilityData.seriesId || selectedEvent.seriesId}`
      : `availability/${selectedEvent.id}`;
    try {
      setIsSubmitting(true);
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/${apiUrl}`,
        availabilityData,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      if (response.data.success) {
        const message = updateAll
          ? availabilityData.isRecurring === false
            ? "Future recurring instances removed. This block is now one-time."
            : "The entire series was updated successfully."
          : "This availability was updated successfully.";
        toast.success(message);
        setShowEditModal(false);
        setSelectedEvent(null);
        await fetchAvailability();
      } else {
        toast.error(response.data.message || "Failed to update availability");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to update availability. Make sure it does not overlap existing blocks.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const todayKey = moment().format("YYYY-MM-DD");

  return (
    <div className="pp-availability-tab">
      <div className="pp-section pp-availability-section">
        {!hideSectionTitle && (
          <div className="pp-section-header">
            <div>
              <h3>Availability</h3>
            </div>
          </div>
        )}

        <div className="pp-section-body pp-availability-body">
          <div className="pp-availability-toolbar">
            <div className="pp-availability-nav">
              <span className="pp-availability-range">{weekRangeLabel}</span>
              <button
                type="button"
                className="pp-availability-nav-btn"
                onClick={goPrev}
                aria-label="Previous week"
              >
                <FaChevronLeft size={12} />
              </button>
              <button
                type="button"
                className="pp-availability-nav-btn"
                onClick={goNext}
                aria-label="Next week"
              >
                <FaChevronRight size={12} />
              </button>
              <button
                type="button"
                className="pp-availability-today-btn"
                onClick={goToday}
              >
                Today
              </button>
            </div>

            <div className="pp-availability-toolbar-right">
              <div
                className="pp-availability-stats"
                aria-live="polite"
                aria-atomic="true"
              >
                <span>
                  This week <strong>{weekStats.hours}h</strong>
                </span>
                <span className="pp-availability-stats-divider" aria-hidden="true">
                  |
                </span>
                <span>
                  Days <strong>{weekStats.days}</strong>
                </span>
              </div>
              {canManage && (
                <button
                  type="button"
                  className="pp-availability-add-btn"
                  onClick={() => {
                    openAddForDay(moment());
                  }}
                >
                  <FaPlus size={12} /> Add Availability
                </button>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="pp-availability-loading">
              Loading availability...
            </div>
          ) : (
            <div className="pp-week-calendar">
              <div className="pp-week-grid-scroll">
                <div className="pp-week-header">
                  <div className="pp-week-gutter" />
                  {weekDays.map((day) => {
                    const key = day.format("YYYY-MM-DD");
                    const isToday = key === todayKey;
                    return (
                      <div
                        key={key}
                        className={`pp-week-day-head ${isToday ? "is-today" : ""}`}
                      >
                        <span className="pp-week-day-name">
                          {DAY_LABELS[day.day()]}
                        </span>
                        <span className="pp-week-day-num">{day.date()}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="pp-week-grid">
                  <div className="pp-week-hours">
                    {HOURS.map((hour) => (
                      <div key={hour} className="pp-week-hour-label">
                        {formatHourLabel(hour)}
                      </div>
                    ))}
                  </div>

                  {weekDays.map((day) => {
                    const key = day.format("YYYY-MM-DD");
                    const isToday = key === todayKey;
                    const daySlots = slotsByDay[key] || [];
                    return (
                      <div
                        key={key}
                        className={`pp-week-day-col ${isToday ? "is-today" : ""}`}
                        onClick={(e) => openAddForDay(day, e)}
                        role={canManage ? "button" : undefined}
                        tabIndex={canManage ? 0 : undefined}
                        onKeyDown={(e) => {
                          if (canManage && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            openAddForDay(day);
                          }
                        }}
                      >
                        {HOURS.map((hour) => (
                          <div key={hour} className="pp-week-hour-slot" />
                        ))}
                        {daySlots.map((slot) => {
                          const style = blockStyle(
                            slot.startDate,
                            slot.startTime,
                            slot.endTime,
                          );
                          if (!style) return null;
                          const hours = durationHours(
                            slot.startDate,
                            slot.startTime,
                            slot.endTime,
                          );
                          return (
                            <button
                              type="button"
                              key={slot.id}
                              className="pp-week-block"
                              style={style}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(slot);
                              }}
                              title="Click to edit · Use × to remove"
                            >
                              <span
                                className="pp-week-block-remove"
                                role="button"
                                tabIndex={0}
                                aria-label="Remove availability"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteEventData({
                                    id: slot.id,
                                    seriesId: slot.seriesId,
                                    isRecurring: slot.isRecurring,
                                    startDate: slot.startDate,
                                    endDate: slot.startDate,
                                    startTime: slot.startTime,
                                    endTime: slot.endTime,
                                    title: slot.title || "Available",
                                  });
                                  setShowDeleteModal(true);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDeleteEventData({
                                      id: slot.id,
                                      seriesId: slot.seriesId,
                                      isRecurring: slot.isRecurring,
                                      startDate: slot.startDate,
                                      endDate: slot.startDate,
                                      startTime: slot.startTime,
                                      endTime: slot.endTime,
                                      title: slot.title || "Available",
                                    });
                                    setShowDeleteModal(true);
                                  }
                                }}
                              >
                                ×
                              </span>
                              {(slot.title || slot.name) && (
                                <span className="pp-week-block-name">
                                  {slot.title || slot.name}
                                </span>
                              )}
                              <span className="pp-week-block-time">
                                {formatDisplayTime(
                                  slot.startDate,
                                  slot.startTime,
                                )}
                              </span>
                              <span className="pp-week-block-duration">
                                Duration: {formatDurationLabel(hours)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="pp-availability-legend">
            <span className="pp-availability-legend-badge">Available</span>
            <span className="pp-availability-legend-text">
              · Click any column to add a block · Click a block to edit or remove
              it.
            </span>
          </div>
        </div>
      </div>

      {showAddModal && (
        <AddAvailabilityDrawer
          existingSlots={slots}
          initialData={selectedEvent || {}}
          isOpen={showAddModal}
          isSubmitting={isSubmitting}
          onClose={() => {
            setSelectedEvent(null);
            setShowAddModal(false);
          }}
          onSubmit={handleAddAvailability}
        />
      )}

      {showEditModal && (
        <EditAvailabilityDrawer
          authToken={authToken}
          existingSlots={slots}
          fetchAvailability={fetchAvailability}
          initialData={selectedEvent}
          isOpen={showEditModal}
          isSubmitting={isSubmitting}
          onClose={() => {
            setSelectedEvent(null);
            setShowEditModal(false);
          }}
          onSubmit={handleUpdateAvailability}
          providerId={providerId}
        />
      )}

      {showDeleteModal && deleteEventData && (
        <DeleteAvailabilityModal
          authToken={authToken}
          calendarRef={{ current: null }}
          deleteEventData={deleteEventData}
          fetchAvailability={fetchAvailability}
          onClose={() => {
            setDeleteEventData(null);
            setShowDeleteModal(false);
          }}
          providerId={providerId}
        />
      )}
    </div>
  );
};

export default ProviderAvailabilityTab;
